/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useServices } from "../lib/react-query/useServices";
import { useProviders } from "../lib/react-query/useProviders";
import { useCategories } from "../lib/react-query/useCategories";
import { useConfig } from "../lib/react-query/useConfig";
import usePluginUtils from "./usePluginUtils";
import { useBooking } from "./useBooking";

export const usePreparedClasses = () => {
  const { selectedLocation } = useBooking();
  const { isPluginEnabled } = usePluginUtils();
  const { data: config } = useConfig();
  const categoriesAreEnabled = isPluginEnabled("event_category");

  const { isLoading: loadingClasses, data: classesData } = useServices({
    "filter[type]": "class",
    ...(isPluginEnabled("location") && { "filter[location_id]": selectedLocation?.id }),
  });
  // Not filtered by type: the list has to include each class's own provider.
  const { isLoading: loadingProviders, data: providersData } = useProviders({
    "filter[location_id]": selectedLocation?.id,
  });
  const { isLoading: loadingCategories, data: categoriesData } = useCategories({ enabled: categoriesAreEnabled });

  // A class is bookable only with its own provider, whose qty is the class capacity
  // (further capped by the company-wide group limit).
  const classes = useMemo(() => {
    if (!classesData || !providersData) return [];

    return classesData.flatMap((item) => {
      const provider = providersData.find((p) => (item.providers || []).includes(p.id));
      if (!provider) return [];

      return [
        {
          ...item,
          provider,
          limit_booking: Math.min(provider.qty, config.max_group_bookings || provider.qty),
        },
      ];
    });
  }, [classesData, providersData, config.max_group_bookings]);

  const categoriesWithClasses = useMemo(() => {
    if (!categoriesAreEnabled || !categoriesData) return [];

    return categoriesData
      .map((category) => ({
        ...category,
        services: category.services.map((id) => classes.find((item) => item.id === id)).filter(Boolean),
      }))
      .filter((category) => category.services.length);
  }, [categoriesAreEnabled, categoriesData, classes]);

  return {
    classes,
    categoriesWithClasses,
    isLoading: loadingClasses || loadingProviders || (categoriesAreEnabled && loadingCategories),
    noDataToDisplay: !classes.length,
  };
};
